import axiosInterceptor from '../utils/axios/axiosInterceptor';
import type { DatabaseType, PostgresSettings } from '../types/onboarding';
import {
  AddOrgUserRequest,
  AddOrgUserResponse,
  AddProjectResponse,
  AddProjectUserRequest,
  AddProjectUserResponse,
  AddUserRequest,
  AddUserResponse,
  BackendProjectDTO,
  BackendUserDTO,
  BackendUserMeDTO,
  GetUserByEmailResponse,
  ListMyProjectsResponse,
  ListOrganizationsResponse,
  ListProjectsProvisionStatusResponse,
  ListProjectsResponse,
  ListProjectUsersResponse,
  ListUsersResponse,
  PaginatedMeta,
  ProvisionInfrastructureRequest,
  ProvisionInfrastructureResponse,
  UpdateProjectRequest,
  UpdateProjectResponse,
  UpdateUserRequest,
  UpdateUserResponse,
} from './onboardingTypes';

const api = () => axiosInterceptor(true);

export const DEFAULT_LIST_PAGE_SIZE = 25;
export const DEFAULT_PROJECT_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;
export const DEFAULT_USER_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;
export const DEFAULT_RUN_PAGE_SIZE = DEFAULT_LIST_PAGE_SIZE;
export const MAX_PAGE_SIZE = 100;

export async function getMe(): Promise<BackendUserMeDTO> {
  const response = await api().get<BackendUserMeDTO>('/user/me');
  return response.data;
}

export async function switchActiveOrganization(
  organizationId: number
): Promise<BackendUserMeDTO> {
  const response = await api().patch<BackendUserMeDTO>('/user/active-organization', {
    organization_id: organizationId,
  });
  return response.data;
}

export async function listProjects(params?: {
  pageNumber?: number;
  pageSize?: number;
  organizationId?: number;
  assignedUserId?: number;
  search?: string;
  includeAssignees?: boolean;
  signal?: AbortSignal;
}): Promise<ListProjectsResponse> {
  const response = await api().get<ListProjectsResponse>('/admin/projects', {
    signal: params?.signal,
    params: {
      page_number: params?.pageNumber ?? 0,
      page_size: params?.pageSize ?? DEFAULT_PROJECT_PAGE_SIZE,
      ...(params?.organizationId !== undefined
        ? { organization_id: params.organizationId }
        : {}),
      ...(params?.assignedUserId !== undefined
        ? { assigned_user_id: params.assignedUserId }
        : {}),
      ...(params?.search ? { search: params.search } : {}),
      ...(params?.includeAssignees === false
        ? { include_assignees: 'false' }
        : {}),
    },
  });
  return response.data;
}

/** Paginate through projects assigned to a user within an organization. */
export async function listUserAssignedProjectIds(params: {
  organizationId: number;
  userId: number;
  signal?: AbortSignal;
}): Promise<string[]> {
  const ids: string[] = [];
  let pageNumber = 0;
  let hasMore = true;
  const maxPages = 100;

  while (hasMore && pageNumber < maxPages) {
    const response = await listProjects({
      organizationId: params.organizationId,
      assignedUserId: params.userId,
      includeAssignees: false,
      pageNumber,
      pageSize: DEFAULT_PROJECT_PAGE_SIZE,
      signal: params.signal,
    });
    for (const project of response.projects ?? []) {
      ids.push(String(project.project_id));
    }
    hasMore = response.has_more ?? false;
    pageNumber += 1;
  }

  return ids;
}

export async function listProjectsProvisionStatus(
  projectIds: number[],
  signal?: AbortSignal
): Promise<ListProjectsProvisionStatusResponse> {
  if (projectIds.length === 0) {
    return { statuses: [] };
  }
  const response = await api().get<ListProjectsProvisionStatusResponse>(
    '/admin/projects/provision-status',
    {
      signal,
      params: { project_ids: projectIds.join(',') },
    }
  );
  return response.data;
}

export async function listOrganizations(
  signal?: AbortSignal
): Promise<ListOrganizationsResponse> {
  const response = await api().get<ListOrganizationsResponse>(
    '/admin/organizations',
    { signal }
  );
  return response.data;
}

export async function listUsers(params?: {
  pageNumber?: number;
  pageSize?: number;
  organizationId?: number;
  search?: string;
  signal?: AbortSignal;
}): Promise<ListUsersResponse> {
  const response = await api().get<ListUsersResponse>('/admin/users', {
    signal: params?.signal,
    params: {
      page_number: params?.pageNumber ?? 0,
      page_size: params?.pageSize ?? DEFAULT_USER_PAGE_SIZE,
      ...(params?.organizationId !== undefined
        ? { organization_id: params.organizationId }
        : {}),
      ...(params?.search ? { search: params.search } : {}),
    },
  });
  return response.data;
}

export async function getUserByEmail(
  email: string,
  signal?: AbortSignal
): Promise<BackendUserDTO | null> {
  const response = await api().get<GetUserByEmailResponse>(
    '/admin/users/by-email',
    {
      signal,
      params: { email: email.trim().toLowerCase() },
    }
  );
  return response.data.user ?? null;
}

export async function listProjectUsers(
  projectId: number
): Promise<ListProjectUsersResponse> {
  const response = await api().get<ListProjectUsersResponse>(
    '/admin/project-user',
    { params: { project_id: projectId } }
  );
  return response.data;
}

export async function listMyProjects(params?: {
  pageNumber?: number;
  pageSize?: number;
  search?: string;
  signal?: AbortSignal;
}): Promise<ListMyProjectsResponse> {
  const response = await api().get<ListMyProjectsResponse>('/user/projects', {
    signal: params?.signal,
    params: {
      page_number: params?.pageNumber ?? 0,
      page_size: params?.pageSize ?? DEFAULT_PROJECT_PAGE_SIZE,
      ...(params?.search ? { search: params.search } : {}),
    },
  });
  return response.data;
}

export async function getProjectById(
  projectId: number
): Promise<BackendProjectDTO | null> {
  try {
    const response = await api().get<{ project: BackendProjectDTO }>(
      `/admin/project/${projectId}`
    );
    return response.data.project ?? null;
  } catch (err: unknown) {
    const status = (err as { response?: { status?: number } })?.response?.status;
    if (status === 404) return null;
    throw err;
  }
}

export type { PaginatedMeta };

export async function addProject(payload: {
  project_name: string;
  organization_name: string;
  start_date: string;
  end_date?: string;
  database_type?: DatabaseType;
  /** postgres only: the client's PostgreSQL major version, which the targets run. */
  engine_major_version?: number;
  /** postgres only: the one schema the project uses (backend default: public). */
  schema_name?: string;
  assign_user_emails?: string[];
}): Promise<AddProjectResponse> {
  const response = await api().post<AddProjectResponse>(
    '/admin/add-project',
    payload
  );
  return response.data;
}

export async function removeOrgUser(payload: {
  email: string;
  organisation_name: string;
}): Promise<{ message: string }> {
  const response = await api().delete<{ message: string }>('/admin/org-users', {
    params: {
      email: payload.email,
      organisation_name: payload.organisation_name,
    },
  });
  return response.data;
}

export async function addOrgUser(
  payload: AddOrgUserRequest
): Promise<AddOrgUserResponse> {
  const response = await api().post<AddOrgUserResponse>(
    '/admin/org-users',
    payload
  );
  return response.data;
}

export async function addUser(
  payload: AddUserRequest
): Promise<AddUserResponse> {
  const response = await api().post<AddUserResponse>('/admin/users', payload);
  return response.data;
}

export async function addProjectUser(
  payload: AddProjectUserRequest
): Promise<AddProjectUserResponse> {
  const response = await api().post<AddProjectUserResponse>(
    '/admin/project-user',
    payload
  );
  return response.data;
}

export async function updateUser(
  userId: number,
  payload: UpdateUserRequest
): Promise<UpdateUserResponse> {
  const response = await api().patch<UpdateUserResponse>(
    `/users/${userId}`,
    payload
  );
  return response.data;
}

export async function updateProject(
  projectId: number,
  payload: UpdateProjectRequest
): Promise<UpdateProjectResponse> {
  const response = await api().patch<UpdateProjectResponse>(
    `/admin/project/${projectId}`,
    payload
  );
  return response.data;
}

export async function provisionInfrastructure(
  payload: ProvisionInfrastructureRequest
): Promise<ProvisionInfrastructureResponse> {
  const response = await api().post<ProvisionInfrastructureResponse>(
    '/admin/provision-infrastructure',
    payload
  );
  return response.data;
}

export async function deleteProject(projectId: number): Promise<void> {
  await api().delete(`/admin/project/${projectId}`);
}

/** Also used to change only a postgres project's version; the targets are then re-provisioned. */
export async function changeDatabaseType(
  projectId: number,
  databaseType: DatabaseType,
  postgres?: PostgresSettings
): Promise<void> {
  await api().post(`/admin/project/${projectId}/change-database-type`, {
    database_type: databaseType,
    ...(postgres && {
      engine_major_version: postgres.engineMajorVersion,
      schema_name: postgres.schemaName,
    }),
  });
}
